import {
  requestSameRaceMultiOdds,
  type srmError,
} from "utilities/api/picklebet/betting";
import { attempt } from "@/utilities/attempt";
import { ok, err, type ResultAsync } from "@/utilities/result";

export type SEMPricingError =
  | { kind: "not-priceable"; messages: string[] }
  | { kind: "no-odds" }
  | { kind: "transport"; cause: unknown };

export type SEMPrice = {
  odds: number;
  // Legs the server priced around because the other legs already imply them.
  redundantOutcomeIds: string[];
};

type WireError = { messageTemplate: string; message: string };

// A redundancy warning names its leg in the path: bets.one.selections.<outcomeId>
const redundantOutcomeIdsIn = (errors: srmError[]): string[] =>
  errors
    .filter((error) => error.messageTemplate === "market.redundant.selection")
    .map((error) => error.propertyPath?.split(".")[3])
    .filter((outcomeId): outcomeId is string => Boolean(outcomeId));

// The API has two ways of saying "can't price this": HTTP 400 with an
// `errors` array (thrown by the fetch wrapper), or 200 with the same
// array inside the body. Both map to `not-priceable`. A 200 whose errors
// are all redundancy warnings still carries a price, so it stays priced.
export const fetchSEMOdds = async (params: {
  outcomeIds: string[];
  token: string;
}): ResultAsync<SEMPrice, SEMPricingError> => {
  const { outcomeIds, token } = params;

  const response = await attempt(
    requestSameRaceMultiOdds({
      token,
      payload: { bets: { one: { selections: [{ outcomeIds }] } } },
    }),
  );

  if (!response.ok) {
    const errs = (response.error as { errors?: WireError[] })?.errors;
    if (Array.isArray(errs) && errs.length > 0) {
      return err({
        kind: "not-priceable",
        messages: errs.map((e) => e.messageTemplate),
      });
    }
    return err({ kind: "transport", cause: response.error });
  }

  const result = response.data?.bets?.one;
  if (!result) return err({ kind: "no-odds" });
  const errors = result.errors ?? [];
  const redundantOutcomeIds = redundantOutcomeIdsIn(errors);
  if (redundantOutcomeIds.length !== errors.length) {
    return err({
      kind: "not-priceable",
      messages: errors.map((error) => error.messageTemplate),
    });
  }
  if (!result.odds) return err({ kind: "no-odds" });
  return ok({ odds: result.odds, redundantOutcomeIds });
};
